import { Expose } from 'class-transformer';
import { PccAuditInfo } from './audit-info.typings';

export type PccCarePlanStatus = 'Active' | 'Closed';
export type PccCarePlanFocusStatus = 'Active' | 'Resolved' | 'Closed';

export class PccCarePlanParams {
	carePlanStatuses?: PccCarePlanStatus[];

	focusStatuses?: PccCarePlanFocusStatus[];
}

/**
 * Represents an intervention within a focus of a care plan.
 */
export class PccCarePlanFocusIntervention extends PccAuditInfo {
	/**
	 * PointClickCare's unique identifier for an intervention.
	 */
	interventionId: number;

	/**
	 * Instruction for the intervention.
	 */
	instruction: string;

	/**
	 * Represents the status of the intervention. Possible values are "Active", "Resolved", and "Cancelled".
	 */
	status: PccCarePlanFocusStatus;

	/**
	 * Description of the intervention.
	 */
	description: string;

	/**
	 * Represents the frequency of the intervention. If the frequency is not part of the standard frequencies, the value is 'Custom'.
	 */
	frequency: string;

	/**
	 * Represents the details of the intervention.
	 */
	details: string;

	constructor(partial?: Partial<PccCarePlanFocusIntervention>) {
		super();
		Object.assign(this, partial);
	}

	@Expose({ name: 'details' })
	getDetails() {
		const details = [];
		if (this.description) details.push(`Description: ${this.description}`);
		if (this.instruction) details.push(`Instruction: ${this.instruction}`);
		if (this.frequency) details.push(`Frequency: ${this.frequency}`);
		if (this.status) details.push(`Status: ${this.status}`);
		return details.join(', ');
	}
}

export class PccCarePlanFocusGoal extends PccAuditInfo {
	/**
	 * PointClickCare's unique identifier for a care plan goal.
	 */
	goalId: number;

	/**
	 * Description of the goal.
	 */
	description: string;

	/**
	 * Represents the status of the goal. Possible values are "Active", "Resolved", and "Closed".
	 */
	status: 'Active' | 'Resolved' | 'Closed';

	/**
	 * Represents the date and time when the goal is targeted for resolution.
	 */
	targetDate: string;

	/**
	 * Represents the details of the goal.
	 */
	details: string;

	constructor(partial?: Partial<PccCarePlanFocusGoal>) {
		super();
		Object.assign(this, partial);
	}

	@Expose({ name: 'details' })
	getDetails() {
		const details = [];
		if (this.description) details.push(`Description: ${this.description}`);
		if (this.targetDate) details.push(`Target Date: ${this.targetDate}`);
		if (this.status) details.push(`Status: ${this.status}`);
		return details.join(', ');
	}
}

/**
 * Represents a focus within a care plan.
 */
export class PccCarePlanFocus extends PccAuditInfo {

	/**
	 * Description of the focus.
	 * @type {string}
	 */
	description: string;

	/**
	 * PointClickCare's unique identifier for a care plan focus.
	 * @type {number}
	 */
	focusId: number;

	/**
	 * Represents the status of the focus. Possible values are "Active", "Resolved", and "Closed".
	 * @type {string}
	 */
	status: PccCarePlanFocusStatus;

	/**
	 * List of goals which are part of the focus.
	 * @type {PccCarePlanFocusGoal[]}
	 * @optional
	 */
	goals?: PccCarePlanFocusGoal[];

	/**
	 * List of interventions which are part of the focus.
	 * @type {PccCarePlanFocusIntervention[]}
	 * @optional
	 */
	interventions?: PccCarePlanFocusIntervention[];

	/**
	 * Represents the details of the focus.
	 */
	details: string;

	constructor(partial?: Partial<PccCarePlanFocus>) {
		super();
		Object.assign(this, partial);

		if (this.goals) this.goals = this.goals.map((goal) => new PccCarePlanFocusGoal(goal));
		if (this.interventions) this.interventions = this.interventions.map((intervention) => new PccCarePlanFocusIntervention(intervention));
	}

	@Expose({ name: 'details' })
	getDetails(prefix = '', focusIndex = -1) {
		const details = [];
		if (this.description) details.push(`${prefix}Description: ${this.description}`);
		if (this.status) details.push(`${prefix}Status: ${this.status}`);
		if (this.initiatedDate) details.push(`${prefix}Initiated Date: ${this.initiatedDate}`);
		if (this.goals) details.push(`\n${prefix}Goals:\n${this.goals.map((goal) => `${prefix}\t${goal.getDetails()}`).join('\n')}`);
		if (this.interventions) details.push(`\n${prefix}Interventions:\n${this.interventions.map((intervention) => `${prefix}\t${intervention.getDetails()}`).join('\n')}`);
		return `${focusIndex >= 0 ? `\n${prefix}Focus ${focusIndex + 1}:\n` : ''}${details.join(', ')}`;
	}
}

/**
   * Represents a patient's care plan in PointClickCare.
   *
   * SimpliScreen maps the care plan to the following string for embedding in the RAG:
   * closedBy: {closedBy}, closedDate: {closedDate}, closureReason: {closureReason}, facId: {facId}, focuses: {focuses}, nextReviewDate: {nextReviewDate}, status: {status}
   * \t Focuses: {description}, status: {status}, initiatedDate: {initiatedDate},
   * \t\t Goals: {description}, targetDate: {targetDate}, status: {status}
   * \t\t Interventions: {description}, instruction: {instruction}, frequency: {frequency}, status: {status}
   *
   * @see https://connect2.pointclickcare.com/api/public/preview1/orgs/{orgUuid}/care-plans?patientId={patientId}
   */
export class PccCarePlan extends PccAuditInfo {
	/**
	 * PointClickCare's unique identifier for a patient's care plan.
	 * @type {number}
	 */
	carePlanId: number;

	/**
	 * Represents the user that closed the care plan.
	 * @type {string}
	 * @optional
	 */
	closedBy?: string;

	/**
	 * Represents the date and time when the care plan was closed.
	 * @type {string} ISO 8601 date-time format.
	 * @optional
	 */
	closedDate?: string;

	/**
	 * Reason for closing the care plan.
	 * @type {string}
	 * @optional
	 */
	closureReason?: string;

	/**
	 * PointClickCare's unique identifier for a facility within the organization.
	 * @type {number}
	 */
	facId: number;

	/**
	 * List of focuses which are part of the care plan.
	 * @type {Focus[]}
	 * @optional
	 */
	focuses?: PccCarePlanFocus[];

	/**
	 * Represents the date and time when the care plan will be reviewed.
	 * @type {string} ISO 8601 date-time format.
	 */
	nextReviewDate: string;

	/**
	 * PointClickCare's unique identifier for a patient within the organization.
	 *
	 * When a patient is admitted to a facility within an organization, they are assigned a patientId which is unique to their stay within that facility. If the same patient were to get admitted to another facility within the same organization in the future, they would be assigned a new patientId. The patientId however is unique at the organization level. That is, no two patients across different facilities within the organization will have the same patientId.
	 *
	 * This identifier is crucial for tracking patient information and care but must not be displayed in any user interface due to legal privacy requirements.
	 * @type {number}
	 */
	patientId: number;

	/**
	 * Represents the status of the care plan. Possible values are "Active" and "Closed".
	 * @type {string}
	 */
	status: PccCarePlanStatus;

	/**
	 * Represents the details of the care plan.
	 */
	details: string;

	constructor(partial?: Partial<PccCarePlan>) {
		super();
		Object.assign(this, partial);
	}

	@Expose({ name: 'details' })
	getDetails() {
		const details = [`Data Type: Care Plan`];
		if (this.carePlanId) details.push(`Care Plan ID: ${this.carePlanId}`);
		if (this.status) details.push(`Status: ${this.status}`);
		if (this.nextReviewDate) details.push(`Next Review Date: ${this.nextReviewDate}`);
		if (this.closedDate) details.push(`Closed Date: ${this.closedDate}`);
		if (this.closedBy) details.push(`Closed By: ${this.closedBy}`);
		if (this.closureReason) details.push(`Closure Reason: ${this.closureReason}`);
		if (this.focuses) details.push(`${this.focuses.map((focus, index) => `${focus.getDetails('\t', index)}`).join('\n')}`);
		return details.join(', ');
	}
}
